import { PayrollResponse } from "@herbjoy/apex-api-models";
import angular from "angular";
import { StateService, Transition } from "angular-ui-router";
import { configureStates, lazyLoad, resolveFromParam } from "../../../../core/router/router-helper.provider";
import { V2_PAYROLL_ENTRY } from "../payroll-entry-flags";
import { PayrollFilters } from "./departments/filters-editor/payroll-entry-filters-editor.component";
import { departmentSectionEnum, PayrollEntryNavigationService } from "./payroll-entry-navigation.service";

// All states will be children of the same parent layout state.
const parent = "payroll.entry.edit.v2";

const routes = [{
    state: parent,
    config: {
        component: "apexPayrollEntryLayout",
        lazyLoad: lazyLoad(() => import("./payroll-entry-v2.module").then(p => p.PayrollEntryV2Module)),
        onEnter: (featureManager, $state, $transition$) => {
            'ngInject';
            const v2Enabled = featureManager.isEnabled(V2_PAYROLL_ENTRY);
            if (!v2Enabled) {
                const { payrollID } = $transition$.params();
                return $state.target("payroll.entry.edit.v1", {
                    payrollID
                });
            }
        },
        redirectTo: (transition) => {
            const params = transition.params();
            return transition.injector().getAsync("payrollCalculations")
                .then(payroll => {
                    if (payroll.Locked) {
                        return {
                            state: `${parent}.summary`
                        }
                    }
                    return {
                        state: `${parent}.department`,
                        params: { ...params, department: departmentSectionEnum.administrative }
                    }

                });
        },
        params: {
            navEnabled: { type: "bool", value: true, dynamic: true }
        },
        resolve: {
            departments: (payrollCalculations, payrollEntryNavigationService) => {
                'ngInject';
                return payrollEntryNavigationService.getDepartments(payrollCalculations);
            },
            isProcessor: employeeService => {
                'ngInject';
                return employeeService.retrievePermissions({
                    stateName: "report.payrollProcessing"
                }).then(access => access.CanUpdate);
            },
            navEnabled: resolveFromParam("navEnabled"),
            onChangeDepartment: $state => {
                'ngInject';
                return ["$event", ({ department, view }) => {
                    return $state.go(`${parent}.department`, {
                        department: department.value,
                        searchTerm: "",
                        // Either specify the view by value, or forward the falsy value (either null or undefined)
                        view: view ? view.value : view,
                        page: 1 // Always go back to page 1 when changing departments.
                    }, { location: "replace" });
                }]
            },
            onChangeView: $state => {
                'ngInject';
                return ["$event", ({ view }) => {
                    return $state.go(`${parent}.department`, {
                        view: view.value
                    }, { location: "replace" });
                }]
            },
            onOpenSummary: $state => {
                'ngInject';
                return () => $state.go(`${parent}.summary`, null, { location: "replace" });
            },
            onSearch: $state => {
                'ngInject';
                return ["$event", ({ searchTerm }) => {
                    return $state.go(`${parent}.department`, { searchTerm, page: 1 }, { location: "replace" })
                }];
            },
            onChangePayrollFilters: ($state: StateService) => {
                'ngInject';
                return ["$event", ({ payrollFilters }: { payrollFilters: PayrollFilters }) => {
                    return $state.go(`${parent}.department`, { ...payrollFilters, page: 1 }, { location: "replace" });
                }]
            },
            onNavigateToPayroll: $state => {
                'ngInject';
                return ["$event", ({ department, view }) => {
                    return $state.go(`${parent}.department`, {
                        department: department ? department : "admins",
                        view: view
                    }, { location: "replace", reload: "payroll.entry.edit" });
                }];
            }

        },
    }
}, {
    // State branch where a specific department is displayed.
    state: `${parent}.department`,
    config: {
        url: "/department/:department?{view}",
        params: {
            searchTerm: { type: "string", value: "", dynamic: true },
            page: { type: "int", value: 1, dynamic: true },
            filterByIncompleteEmployeeRegistration: { type: "bool", value: false, dynamic: true }
        },
        resolve: {
            searchTerm: resolveFromParam("searchTerm"),
            payPeriods: (payrollEntryNavigationService: PayrollEntryNavigationService,
                payrollCalculations: PayrollResponse, searchTerm: string,
                payrollFilters: PayrollFilters) => {
                'ngInject';
                return payrollEntryNavigationService.searchPayrollForEmployees(payrollCalculations, searchTerm,
                    payrollFilters);
            },
            selectedDepartment: (departments, payPeriods, $transition$, payrollEntryNavigationService) => {
                'ngInject';
                return payrollEntryNavigationService.selectDepartment(departments, payPeriods,
                    $transition$.params());
            },
            views: (payrollCalculations, selectedDepartment, payrollEntryNavigationService) => {
                'ngInject';
                return payrollEntryNavigationService.getSectionsForDepartment(payrollCalculations, selectedDepartment);
            },
            page: ($transition$, payPeriods, selectedDepartment) => {
                'ngInject';
                return {
                    pageNumber: resolveFromParam("page")($transition$),
                    pageSize: 50,
                    entriesLength: payPeriods[selectedDepartment.value].length
                };
            },
            selectedView: (views, $transition$, payrollEntryNavigationService) => {
                'ngInject';
                return payrollEntryNavigationService.selectView(views, $transition$.params());
            },
            payrollFilters: ($transition$: Transition): PayrollFilters => {
                'ngInject';
                const { filterByIncompleteEmployeeRegistration } = $transition$.params();
                return {
                    filterByIncompleteEmployeeRegistration
                };
            },
            onSavePayrollEntries($state, $q, payrollService, utilityService) {
                'ngInject';
                return ["$event", ({ payPeriods, payrollID }) => {
                    const dirtyPayrollEntries = payPeriods.filter(p => p.dirty);
                    const request = $q.all(dirtyPayrollEntries.map(payrollEntry => {
                        return payrollService.savePayrollEntry(payrollID, payrollEntry);
                    })).then(() => {
                        const param = {
                            navEnabled: true,
                        };
                        return $state.go('.', param,
                            { location: "replace", reload: "payroll.entry.edit" });
                    });

                    utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(request, "Saving failed"));
                }]
            },
            onCancelChanges($state) {
                'ngInject';
                return ["$event", () => {
                    const param = {
                        navEnabled: true,
                    };
                    return $state.go('.', param,
                        { location: "replace", reload: "payroll.entry.edit" });
                }]
            },
            onChangePage($state) {
                'ngInject';
                return ["$event", ({ page }) => {
                    return $state.go(`${parent}.department`, {
                        page: page.pageNumber
                    }, { location: "replace", reload: "payroll.entry.edit", inherit: true });
                }]
            }
        },
        views: {
            payrollEntrySectionHeader: {
                component: "apexPayrollEntryDepartmentsHeader"
            },
            payrollEntrySectionBody: {
                component: "apexPayrollEntryDepartmentsBody"
            },
            payrollEntrySectionFooter: {
                component: "apexPayrollEntryDepartmentsFooter"
            },
            payrollEntryToolbar: {
                component: "apexPayrollEntryToolbar"
            }
        }
    }
}, {
    // State branch where the summary for the entire payroll is displayed.
    state: `${parent}.summary`,
    config: {
        url: "/summary",
        resolve: {
            onUpdatePayrollMemo: ($state, payrollService, utilityService) => {
                'ngInject';
                return ["$event", ({ payrollID, memo }) => {
                    const payrollMemoUpdated = payrollService.savePayrollMemo(payrollID, memo);
                    utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(payrollMemoUpdated, "Updating memo failed"));

                }]
            },
        },
        views: {
            payrollEntrySectionHeader: {
                component: "apexPayrollEntryDepartmentsHeader",
                bindings: {
                    payroll: "payrollCalculations"
                }
            },
            payrollEntrySectionBody: {
                component: "apexPayrollEntrySummaryBody",
                bindings: {
                    payroll: "payrollCalculations"
                }
            },
            payrollEntrySectionFooter: {
                //Hide the footer because it is not neeed in summary page
            },
            payrollEntryToolbar: {
                component: "apexPayrollEntryToolbar"
            }
        }
    }
},
{
    // State branch where a specific employee's enteries are displayed.
    state: `${parent}.employee`,
    config: {
        url: "/employee/:employeeID",
        params: {
            department: { type: "string", value: "", dynamic: true },
            view: { type: "string", value: "", dynamic: true },
            isEditMode: { type: "bool", value: false }
        },
        resolve: {
            department: resolveFromParam("department"),
            view: resolveFromParam("view"),
            isEditMode: resolveFromParam("isEditMode"),
            payroll: (payrollCalculations) => {
                'ngInject';
                return payrollCalculations;
            },
            payPeriods: (payrollEntryNavigationService, payrollCalculations, $transition$) => {
                'ngInject';
                const employeeID = parseInt($transition$.params().employeeID)
                return payrollEntryNavigationService.getPayrollEntriesByEmployee(payrollCalculations, employeeID);
            },
            selectedEmployee: (payPeriods) => {
                'ngInject';
                if (payPeriods.length > 0) {
                    const defaultPayPeriod = payPeriods[0];
                    return {
                        EmployeeName: defaultPayPeriod.EmployeeFirstName + ' ' + defaultPayPeriod.EmployeeLastName,
                        EmployeeID: defaultPayPeriod.EmployeeID,
                        Entries: defaultPayPeriod.Entries
                    }
                }
            },
            payStub: (payStubService, payrollCalculations, $transition$) => {
                'ngInject';
                const employeeID = parseInt($transition$.params().employeeID);
                return payStubService.retrievePayStub(payrollCalculations.PayrollID, employeeID,
                    payrollCalculations.IcdCorporationID)
            }

        },
        views: {
            payrollEntrySectionBody: {
                component: "apexEditPayrollEntryEmployeeContainer"
            },
            payrollEntryToolbar: {
                component: "apexPayrollEntryToolbar"
            }
        }
    }
}];

/**
 * A module defining the payroll entry v2 state machine.
 */
export const PayrollEntryV2RoutingModule = angular
    .module("apex.mainApp.payroll.entry.v2.routing", [])
    .run(configureStates(routes))
    .name;
